/**
 * What a field that failed validation says, and where focus goes.
 *
 * "Check this field." told an administrator that something was wrong and not
 * what. Each field the administration validates has its own sentence here,
 * keyed by the name the shared Zod schema gives it, so the message under the
 * reason field says to enter a reason and the one under a subject code says
 * what a code may contain.
 */
import type { TranslationKey, Translate } from '@athena/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'

const FIELD_MESSAGES: Readonly<Record<string, TranslationKey>> = {
  reason: 'management.errors.reason',
  name: 'management.errors.name',
  firstName: 'management.errors.firstName',
  lastName: 'management.errors.lastName',
  email: 'management.errors.email',
  code: 'management.errors.code',
  spanishName: 'management.errors.spanishName',
  englishName: 'management.errors.englishName',
}

export interface ValidationIssue {
  path: readonly PropertyKey[]
  code?: string
  maximum?: unknown
}

/**
 * The first issue for each field, as the sentence that field shows.
 *
 * A value that is too long says how long it may be; anything else says what
 * the field needs. An issue on a field with no sentence of its own falls back
 * to the generic one rather than to nothing.
 */
export function fieldErrors(
  issues: readonly ValidationIssue[],
  t: Translate,
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const issue of issues) {
    const field = String(issue.path[0])
    if (field in errors) continue
    errors[field] =
      issue.code === 'too_big' && typeof issue.maximum === 'number'
        ? t('management.errors.tooLong', [String(issue.maximum)])
        : t(FIELD_MESSAGES[field] ?? 'management.errors.generic')
  }
  return errors
}

/**
 * Move focus to the first invalid field once a failed submission has drawn
 * its messages.
 *
 * Call `request()` where validation fails; the focus happens after the
 * render that marks the fields `aria-invalid`, because before it there is
 * nothing to find. Scoped to `ref`, so two forms on one page — the profile
 * and the plan change — never pull focus from each other.
 */
export function useFocusFirstInvalid<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (attempt === 0) return
    ref.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  }, [attempt])

  const request = useCallback(() => setAttempt((count) => count + 1), [])
  return { ref, request }
}
